import type { BlobLike } from "@pdfking/compatibility"
import { BlobArray } from "@pdfking/compatibility"
import {
  type ContentType,
  FileContentGUID,
  type VirtualFile,
} from "@pdfking/pdfengine"
import { Items, IterableToArray } from "@pdfking/tfunc"
import { RequestBlob } from "@pdfking/workflow/actions/pdfengine/Functions"
import url_read_tracked from "@pdfking/workflow/utils/url_read_tracked"
import { url_query_encode } from "@pdfking/workflow/utils/urlutilities"

import get_django_url from "../../utils/get_django_url"
import type { FileObject } from "../types/FileObject"

type UploadResult = {
  type: ContentType
  remote_id: string
  remote_name: string
}

type Signing = {
  url: string
  fields: Record<string, string>
  info: UploadResult
}

type SigningResponse = {
  success: boolean
  file: Signing[]
}

async function get_content(file: VirtualFile): Promise<BlobLike> {
  return RequestBlob(file)
    .then(({ stream }) => IterableToArray(stream))
    .then(BlobArray)
}

async function make_upload_task(
  signing_request: Promise<SigningResponse>,
  file: FileObject,
  index: number
): Promise<UploadResult> {
  const response = await signing_request

  if (response.success) {
    const blob = await get_content(await file.get_file())
    const signing = response.file[index]

    const data = new FormData()

    for (const [k, v] of Items(signing.fields)) {
      data.append(k, v)
    }

    data.append("file", blob as unknown as Blob, file.short_name)

    const xhttp = await url_read_tracked(signing.url, {
      data,
    })

    if (xhttp.status < 299) return signing.info
    throw `Invalid response from server: ${xhttp.status}`
  }

  console.error(response)

  throw `Invalid response from server: ${response}`
}

export default async (files: FileObject[]): Promise<FileObject[]> => {
  const non_remote_files = files.filter(f => !f.file_meta.remote_id)

  if (non_remote_files.length > 0) {
    const query = url_query_encode({
      name: non_remote_files.map(f => f.short_name),
      sha256: await Promise.all(
        non_remote_files.map(f => f.get_file().then(FileContentGUID))
      ),
    })

    const signing_request = get_django_url("upload_s3").then(sign_url =>
      url_read_tracked(sign_url + "?" + query).then(r => r.json())
    )

    await Promise.all(
      non_remote_files.map((f: FileObject, index: number) =>
        make_upload_task(signing_request, f, index).then(
          ({ remote_id, remote_name }) => {
            f.file_meta.remote_id = remote_id
            f.file_meta.remote_name = remote_name
            return f
          }
        )
      )
    )
  }
  return files
}
